import {
  type MaintenanceSettings,
  WORK_ORDER_PRIORITIES,
  WORK_ORDER_PRIORITY_LABELS,
  type WorkOrderPriorityCode,
} from "@station/contracts";
import {
  Alert,
  Button,
  Card,
  Checkbox,
  FormField,
  Input,
  Label,
  Select,
  SkeletonRows,
  useToast,
} from "@station/ui";
import { useMutation, useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type ReactElement, useId, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  useMaintenancePermissions,
  useMaintenanceSettings,
  useRefreshMaintenance,
} from "../../../features/maintenance/queries.js";
import { MaintenancePage } from "../../../features/maintenance/shared.js";
import { api } from "../../../lib/api.js";
import { errorMessage, isApiError } from "../../../lib/errors.js";
import { queryKeys } from "../../../lib/query.js";

export const Route = createFileRoute("/_app/bakim/ayarlar")({ component: MaintenanceSettingsRoute });

/**
 * Bakım ayarları (Prompt 23 web, `GET/PUT /maintenance/settings`): öncelik başına SLA (yanıt / çözüm saati — işletme hedefi,
 * mevzuat değildir), cihaz arızası alarmından otomatik iş emri ve iş emri maliyetinden açılan giderin kategorisi. Yazma
 * `maintenance.manage` KİRACI kapsamlı (sunucu denetler).
 */
function MaintenanceSettingsRoute(): ReactElement {
  const { t } = useTranslation();
  const permissions = useMaintenancePermissions();
  const settings = useMaintenanceSettings(permissions.enabled && permissions.canManage);
  return (
    <MaintenancePage
      title={t("menu.maintenanceSettings")}
      description={t("maintenance.settings.description")}
      requirement="manage"
    >
      {settings.error ? (
        <Alert tone="danger">{errorMessage(settings.error)}</Alert>
      ) : !settings.data ? (
        <SkeletonRows rows={6} />
      ) : (
        <SettingsForm key={settings.data.version} settings={settings.data} />
      )}
    </MaintenancePage>
  );
}

type SlaDraft = Record<WorkOrderPriorityCode, { responseHours: string; resolveHours: string }>;

function SettingsForm({ settings }: { settings: MaintenanceSettings }): ReactElement {
  const { t } = useTranslation();
  const toast = useToast();
  const refresh = useRefreshMaintenance();
  const autoId = useId();
  const [sla, setSla] = useState<SlaDraft>(
    () =>
      Object.fromEntries(
        WORK_ORDER_PRIORITIES.map((priority) => [
          priority,
          {
            responseHours: String(settings.sla[priority]?.responseHours ?? ""),
            resolveHours: String(settings.sla[priority]?.resolveHours ?? ""),
          },
        ]),
      ) as SlaDraft,
  );
  const [auto, setAuto] = useState(settings.autoWorkOrderFromAlarms);
  const [categoryId, setCategoryId] = useState(settings.expenseCategoryId ?? "");
  const [issues, setIssues] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const categories = useQuery({
    queryKey: [...queryKeys.maintenance("settings"), "expense-categories"],
    staleTime: 300_000,
    queryFn: async () =>
      (
        await api.GET("/expense-categories", {
          params: { query: { includeInactive: "false", onlyUtility: "false" } },
        })
      ).data!.items,
    retry: false,
  });
  const save = useMutation({
    mutationFn: async (
      body: Record<WorkOrderPriorityCode, { responseHours: number; resolveHours: number }>,
    ) =>
      (
        await api.PUT("/maintenance/settings", {
          body: {
            sla: body,
            autoWorkOrderFromAlarms: auto,
            expenseCategoryId: categoryId || null,
            version: settings.version,
          },
        })
      ).data!,
    onSuccess: async () => {
      await refresh();
      toast({ tone: "success", title: t("common.saved") });
    },
    onError: async (caught) => {
      if (isApiError(caught, "VERSION_CONFLICT")) await refresh();
      setError(errorMessage(caught));
    },
  });
  return (
    <form
      className="grid gap-4"
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        setError(null);
        const next: Record<string, string> = {};
        const body = {} as Record<WorkOrderPriorityCode, { responseHours: number; resolveHours: number }>;
        for (const priority of WORK_ORDER_PRIORITIES) {
          const response = Number(sla[priority].responseHours);
          const resolve = Number(sla[priority].resolveHours);
          if (!Number.isInteger(response) || response < 1)
            next[`${priority}.response`] = t("maintenance.settings.hoursInvalid");
          if (!Number.isInteger(resolve) || resolve < 1)
            next[`${priority}.resolve`] = t("maintenance.settings.hoursInvalid");
          else if (resolve < response)
            next[`${priority}.resolve`] = t("maintenance.settings.resolveBeforeResponse");
          body[priority] = { responseHours: response, resolveHours: resolve };
        }
        setIssues(next);
        if (Object.keys(next).length === 0) save.mutate(body);
      }}
    >
      {error ? <Alert tone="danger">{error}</Alert> : null}
      <Card className="grid gap-4 p-4">
        <div className="grid gap-1">
          <h2 className="text-base font-semibold">{t("maintenance.settings.slaTitle")}</h2>
          <p className="text-sm text-muted-foreground">{t("maintenance.settings.slaHint")}</p>
        </div>
        <div className="grid gap-3">
          {WORK_ORDER_PRIORITIES.map((priority) => (
            <fieldset key={priority} className="grid items-start gap-3 sm:grid-cols-[10rem_1fr_1fr]">
              <legend className="sr-only">{WORK_ORDER_PRIORITY_LABELS[priority]}</legend>
              <span className="pt-8 text-sm font-medium">{WORK_ORDER_PRIORITY_LABELS[priority]}</span>
              <FormField
                label={t("maintenance.settings.responseHours", {
                  priority: WORK_ORDER_PRIORITY_LABELS[priority],
                })}
                error={issues[`${priority}.response`]}
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="numeric"
                    className="tabular"
                    value={sla[priority].responseHours}
                    onChange={(event) =>
                      setSla({ ...sla, [priority]: { ...sla[priority], responseHours: event.target.value } })
                    }
                  />
                )}
              </FormField>
              <FormField
                label={t("maintenance.settings.resolveHours", {
                  priority: WORK_ORDER_PRIORITY_LABELS[priority],
                })}
                error={issues[`${priority}.resolve`]}
              >
                {(control) => (
                  <Input
                    {...control}
                    inputMode="numeric"
                    className="tabular"
                    value={sla[priority].resolveHours}
                    onChange={(event) =>
                      setSla({ ...sla, [priority]: { ...sla[priority], resolveHours: event.target.value } })
                    }
                  />
                )}
              </FormField>
            </fieldset>
          ))}
        </div>
      </Card>
      <Card className="grid gap-4 p-4">
        <div className="flex items-start gap-2">
          <Checkbox id={autoId} checked={auto} onChange={(event) => setAuto(event.target.checked)} />
          <div className="grid gap-0.5">
            <Label htmlFor={autoId}>{t("maintenance.settings.autoWorkOrder")}</Label>
            <p className="text-xs text-muted-foreground">{t("maintenance.settings.autoWorkOrderHint")}</p>
          </div>
        </div>
        <FormField
          label={t("maintenance.settings.expenseCategory")}
          description={t("maintenance.settings.expenseCategoryHint")}
          className="sm:max-w-md"
        >
          {(control) =>
            categories.data ? (
              <Select {...control} value={categoryId} onChange={(event) => setCategoryId(event.target.value)}>
                <option value="">{t("maintenance.settings.defaultCategory")}</option>
                {categories.data.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.code} · {category.name}
                  </option>
                ))}
              </Select>
            ) : (
              <Input
                {...control}
                readOnly
                value={settings.expenseCategoryName ?? t("maintenance.settings.defaultCategory")}
              />
            )
          }
        </FormField>
      </Card>
      <div className="flex justify-end">
        <Button type="submit" loading={save.isPending}>
          {t("common.save")}
        </Button>
      </div>
    </form>
  );
}
